import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Dialog } from '@angular/cdk/dialog';
import { AnalyticsService, VideoApiService } from '@core/services';
import { catchError, switchMap, tap, EMPTY, of } from 'rxjs';
import { DeclinedDialog } from '@features/disclaimer/declined-dialog/declined-dialog';
import { STORAGE_KEYS } from '@shared/constants';
import { DisclaimerService } from '@features/disclaimer';
import { SharedVideoId } from '@shared/models/user/shared-video-id';
import { AnalyticsEvents } from '@shared/models';
import { GuestInfo, GuestService, OAuthService } from '@core/auth';

@Component({
  selector: 'mai-guest',
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `@if (showDisclaimer()) {
    <div class="blurry-mask"></div>
  }`,
})
export class Guest implements OnInit {
  private readonly authService = inject(OAuthService);
  private readonly guestService = inject(GuestService);
  private readonly dialogService = inject(Dialog);
  private readonly disclaimerService = inject(DisclaimerService);
  private readonly analytics = inject(AnalyticsService);
  private readonly videoApi = inject(VideoApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  showDisclaimer = signal(true);

  ngOnInit(): void {
    const sharedParam = this.route.snapshot.queryParams['video'];
    const videoIds: SharedVideoId[] =
      typeof sharedParam === 'string'
        ? [{ sharedId: sharedParam }]
        : Array.isArray(sharedParam)
          ? sharedParam.map((sharedId) => ({ sharedId }))
          : [];

    const disclaimerAccepted = sessionStorage.getItem(STORAGE_KEYS.DISCLAIMER_ACCEPTED);

    if (this.authService.isGuestWithValidToken()) {
      const guestInfo = this.guestService.getGuestInfo();
      // If current token doesn't include requested video,
      // we remove current guest token in order to provide guest access to only one case
      if (!this.guestService.hasTokenForVideoIds(videoIds)) {
        this.guestService.clearGuestToken();
      }
      // Open disclaimer with pre-filled guest info
      this.openDisclaimer(true, videoIds, guestInfo);
    } else {
      this.authService
        .isAuthenticated()
        .pipe(
          switchMap((isLoggedIn) => {
            if (isLoggedIn) {
              // Logged-in user accessing shared link
              if (!disclaimerAccepted) {
                this.openDisclaimer(false, videoIds);
                return EMPTY;
              } else {
                this.showDisclaimer.set(false);
                this.navigateToVideo(videoIds);
                return EMPTY;
              }
            } else {
              // Guest: show disclaimer with form
              this.openDisclaimer(true, videoIds);
              return EMPTY;
            }
          }),
        )
        .subscribe();
    }
  }

  private openDisclaimer(
    showGuestForm: boolean,
    videoIds: SharedVideoId[],
    guestInfo?: GuestInfo,
  ): void {
    this.disclaimerService
      .openDisclaimer(showGuestForm, videoIds, guestInfo)
      .closed.pipe(
        switchMap((disclaimerData) => {
          const firstSharedId = videoIds[0];
          if (!disclaimerData) {
            this.analytics.trackEvent(AnalyticsEvents.CaseSharedDisclaimerRejected, {
              shared_case_id: firstSharedId.sharedId,
            });
            // Disclaimer rejected
            const cancelRef = this.dialogService.open(DeclinedDialog, {
              ariaLabel: 'Access declined',
            });
            return cancelRef.closed.pipe(
              tap(() => this.openDisclaimer(showGuestForm, videoIds, guestInfo)),
            );
          } else {
            // disclaimer accepted
            this.showDisclaimer.set(false);
            sessionStorage.setItem(STORAGE_KEYS.DISCLAIMER_ACCEPTED, 'accepted');

            if (typeof disclaimerData === 'object') {
              // Guest case
              return this.guestService.createOrUpdateToken(videoIds, disclaimerData).pipe(
                tap(() => {
                  this.analytics.trackEvent(AnalyticsEvents.CaseSharedOpened, {
                    shared_case_id: firstSharedId.sharedId,
                  });
                  // A guest has no library: leaving the case would end the guest access.
                  this.router.navigate(['video-details', firstSharedId.sharedId]);
                }),
              );
            } else {
              // Regular case
              this.navigateToVideo(videoIds);
              return EMPTY;
            }
          }
        }),
      )
      .subscribe();
  }

  private navigateToVideo(videoIds: SharedVideoId[]): void {
    if (videoIds.length !== 1) {
      this.router.navigate(['/']);
      return;
    }
    const { sharedId } = videoIds[0];
    // The real id keeps the case on the tab already open for it instead of a share-code twin.
    this.videoApi
      .get(sharedId)
      .pipe(catchError(() => of(null)))
      .subscribe((video) => this.router.navigate(['video-details', video?.id ?? sharedId]));
  }
}
